import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSortingState } from '@/hooks/useSortingState'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArchiveBoxIcon,
  PencilSquareIcon,
  TrashIcon,
  UserIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline'
import { useColli, useDeleteCollo } from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * Pagina lista colli.
 * 
 * FASE 7.4: Spedizioni Avanzate
 * 
 * Features:
 * - Tabella colli con ricerca
 * - Filtri per spedizione
 * - Visualizzazione destinatario, peso, valore
 * - Azioni rapide (modifica, elimina)
 */
export default function ColliList() {
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useSortingState('colli', 'codice_collo')
  const [deleteId, setDeleteId] = useState(null)

  // Query colli
  const { page, setPage } = useListPagination({ search, ordering })

  const { data: colliData, isLoading, isFetching, error } = useColli({ page, search, ordering })
  const deleteMutation = useDeleteCollo()

  // Handler eliminazione
  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId, {
        onSuccess: () => setDeleteId(null)
      })
    }
  }

  // DRF può restituire array diretto o oggetto paginato con results
  const colli = Array.isArray(colliData) 
    ? colliData 
    : (colliData?.results || [])

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Colli</h1>
          <p className="text-muted-foreground">
            Gestisci i colli delle spedizioni
          </p>
        </div>
        <Button asChild>
          <Link to="/ordini/colli/nuovo">
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo Collo
          </Link>
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Cerca per codice collo, numero ordine..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead field="codice_collo" currentOrdering={ordering} onSort={setOrdering}>
                  Codice Collo
                </SortableTableHead>
                <SortableTableHead field="spedizione__ordine__numero" currentOrdering={ordering} onSort={setOrdering}>Ordine</SortableTableHead>
                <SortableTableHead field="destinatario_nome" currentOrdering={ordering} onSort={setOrdering}>Destinatario</SortableTableHead>
                <SortableTableHead field="peso_kg" currentOrdering={ordering} onSort={setOrdering}>Peso</SortableTableHead>
                <SortableTableHead field="valore_dichiarato_cents" currentOrdering={ordering} onSort={setOrdering}>Valore</SortableTableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : colli.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <ArchiveBoxIcon className="h-12 w-12 text-muted-foreground" />
                      <p className="text-muted-foreground">
                        {search ? 'Nessun collo trovato' : 'Nessun collo presente'}
                      </p>
                      {!search && (
                        <Button asChild variant="outline" size="sm">
                          <Link to="/ordini/colli/nuovo">
                            <PlusIcon className="mr-2 h-4 w-4" />
                            Crea il primo collo
                          </Link>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                colli.map((collo) => (
                  <TableRow key={collo.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ArchiveBoxIcon className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-mono font-medium">{collo.codice_collo}</div>
                          <div className="text-sm text-muted-foreground">
                            {collo.numero_collo_display}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {collo.spedizione_ordine_numero ? (
                        <Link 
                          to={`/ordini/${collo.spedizione_ordine_id}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {collo.spedizione_ordine_numero}
                        </Link>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <UserIcon className="h-3 w-3 text-muted-foreground" />
                        {collo.destinatario_nome}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <ScaleIcon className="h-3 w-3 text-muted-foreground" />
                        {collo.peso_kg} kg
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">
                        {formatCurrency(collo.valore_dichiarato_cents)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/ordini/colli/${collo.id}/modifica`}>
                            <PencilSquareIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteId(collo.id)}
                        >
                          <TrashIcon className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Statistiche */}
      {colli.length > 0 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{colli.length}</div>
              <p className="text-xs text-muted-foreground">Totale Colli</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">
                {colli.reduce((sum, c) => sum + parseFloat(c.peso_kg || 0), 0).toFixed(2)} kg
              </div>
              <p className="text-xs text-muted-foreground">Peso Totale</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">
                {formatCurrency(colli.reduce((sum, c) => sum + (c.valore_dichiarato_cents || 0), 0))}
              </div>
              <p className="text-xs text-muted-foreground">Valore Totale</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog conferma eliminazione */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo collo? 
              {' '}Questa azione non può essere annullata.
              {' '}Il peso totale e il numero colli della spedizione verranno ricalcolati automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ListPagination data={colliData} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
